import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Card, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, TablePagination, TextField, Typography, Chip, IconButton,
    InputAdornment, Button, Avatar, Stack, Tooltip, Divider, Breadcrumbs,
    Link, Skeleton, Dialog, DialogTitle, DialogContent, Drawer,
    Grid
} from '@mui/material';
import {
    Search as SearchIcon,
    Edit as EditIcon,
    Add as AddIcon,
    ChevronRight,
    Visibility,
    Close as CloseIcon,
    Business as BusinessIcon,
    LocationOn as LocationIcon,
    AccountBalance as BankIcon,
    VerifiedUser as VerifiedUserIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    HourglassTop as HourglassTopIcon
} from '@mui/icons-material';
import { FetchCompanyListService, ApproveCompanyService } from '../../../utils/services/product.service';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../redux/reducer/snackbarSlice';
import useDebounce from '../../../hooks/useDebounce';

const CompanyList = () => {
    const navigate = useNavigate();

    // State
    const [companies, setCompanies] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const debouncedSearch = useDebounce(searchQuery, 500);
    const [approvalFilter, setApprovalFilter] = useState<'all' | 'approved' | 'rejected' | 'submitted'>('all');

    // Status Dialog State
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState<any>(null);
    const [approvalRemark, setApprovalRemark] = useState('');
    const [approvalLoading, setApprovalLoading] = useState(false);
    const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
    const [detailsCompany, setDetailsCompany] = useState<any>(null);
    const dispatch = useDispatch();

    const inputStyles = {
        '& .MuiOutlinedInput-root': { bgcolor: '#f6f8f7', border: 'none', borderRadius: '0.75rem' },
        '& fieldset': { border: 'none' }
    };

    const fetchCompanies = async () => {
        setLoading(true);
        const payload = {
            offset: page * rowsPerPage,
            limit: rowsPerPage,
            search: debouncedSearch,
            search_key: 'company_name',
            search_value: debouncedSearch,
            ...(approvalFilter !== 'all' ? { approval_status: approvalFilter } : {}),
        };
        try {
            const { code, data } = await FetchCompanyListService(payload);
            if (code === 200 && data?.data) {
                setCompanies(data.data);
                setTotalCount(data.total_count);
            } else {
                setCompanies([]);
                setTotalCount(0);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCompanies();
    }, [page, rowsPerPage, debouncedSearch, approvalFilter]);

    useEffect(() => {
        setPage(0);
    }, [debouncedSearch, approvalFilter]);

    const handleOpenStatusDialog = (company: any) => {
        setSelectedCompany(company);
        setApprovalRemark('');
        setStatusDialogOpen(true);
    };

    const handleCloseStatusDialog = () => {
        setStatusDialogOpen(false);
        setSelectedCompany(null);
    };

    const handleOpenDetails = (company: any) => {
        setDetailsCompany(company);
        setDetailsDrawerOpen(true);
    };

    const handleCloseDetails = () => {
        setDetailsDrawerOpen(false);
        setDetailsCompany(null);
    };

    const handleEdit = (uuid: string) => {
        navigate(`/admin/company/create?uuid=${uuid}`);
    };

    const handleApprovalUpdate = async (status: "approved" | "rejected" | "submitted") => {
        if (!selectedCompany?.company_uuid) return;
        if (!approvalRemark?.trim()) {
            dispatch(showSnackbar({ type: 'error', message: 'Remark is required.' }));
            return;
        }
        setApprovalLoading(true);
        const payload = {
            company_uuid: selectedCompany.company_uuid,
            approval_status: status,
            remark: approvalRemark.trim(),
        };
        const { code, message } = await ApproveCompanyService(payload);
        if (code === 200) {
            dispatch(showSnackbar({ type: 'success', message: 'Company approval status updated.' }));
            fetchCompanies();
            handleCloseStatusDialog();
        } else {
            dispatch(showSnackbar({ type: 'error', message: message || 'Failed to update approval status.' }));
        }
        setApprovalLoading(false);
    };

    const getApprovalStatus = (status?: string) => {
        const normalized = (status || 'submitted').toLowerCase();
        if (normalized === 'approved') return 'approved';
        if (normalized === 'rejected') return 'rejected';
        return 'submitted';
    };

    const getStatusChipProps = (status?: string) => {
        const normalized = getApprovalStatus(status);
        if (normalized === 'approved') {
            return { label: 'Approved', color: 'success' as const, icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> };
        }
        if (normalized === 'rejected') {
            return { label: 'Rejected', color: 'error' as const, icon: <CancelIcon sx={{ fontSize: 16 }} /> };
        }
        return { label: 'Submitted', color: 'warning' as const, icon: <HourglassTopIcon sx={{ fontSize: 16 }} /> };
    };

    // Helper to render Detail Rows in Drawer
    const DetailItem = ({ label, value }: { label: string, value: any }) => (
        <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ color: '#4c9a74', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {label.replace(/_/g, ' ')}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, color: '#0d1b15' }}>
                {value === true ? 'Yes' : value === false ? 'No' : value || 'N/A'}
            </Typography>
        </Box>
    );

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, backgroundColor: '#f6f8f7', minHeight: '100vh' }}>
            {/* Breadcrumbs */}
            <Breadcrumbs separator={<ChevronRight fontSize="small" sx={{ color: '#4c9a74' }} />} sx={{ mb: 2 }}>
                <Link underline="hover" sx={{ color: '#4c9a74', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer' }}>Dashboard</Link>
                <Typography sx={{ color: '#0d1b15', fontSize: '0.875rem', fontWeight: 600 }}>Companies</Typography>
            </Breadcrumbs>

            {/* Page Heading */}
            <Box display="flex" justifyContent="space-between" alignItems="flex-end" mb={4} gap={3}>
                <Box>
                    <Typography variant="h3" sx={{ fontWeight: 900, color: '#0d1b15', letterSpacing: '-0.02em' }}>Companies</Typography>
                    <Typography sx={{ color: '#4c9a74', mt: 1 }}>Manage and track registered enterprises in the compliance network.</Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/admin/company/create')}
                    sx={{ bgcolor: '#0fbd69', borderRadius: '0.75rem', px: 3, height: 44, fontWeight: 700, textTransform: 'none' }}
                >
                    New Company
                </Button>
            </Box>

            <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #e7f3ed', overflow: 'hidden' }}>
                <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2, bgcolor: '#fff', borderBottom: '1px solid #e7f3ed' }}>
                    <TextField
                        size="small"
                        placeholder="Search companies..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && fetchCompanies()}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#4c9a74' }} /></InputAdornment>,
                        }}
                        sx={{ ...inputStyles, width: 350 }}
                    />
                    <TextField
                        select
                        size="small"
                        label="Approval Status"
                        value={approvalFilter}
                        onChange={(e) => setApprovalFilter(e.target.value as typeof approvalFilter)}
                        SelectProps={{ native: true }}
                        sx={{ ...inputStyles, width: 220 }}
                    >
                        <option value="all">All</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="submitted">Submitted</option>
                    </TextField>
                </Box>

                <TableContainer>
                    <Table sx={{ minWidth: 1100 }}>
                        <TableHead sx={{ bgcolor: '#f8fbfa' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase' }}>Company Details</TableCell>
                                <TableCell sx={{ fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase' }}>Contact Info</TableCell>
                                <TableCell sx={{ fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase' }}>GST / PAN</TableCell>
                                <TableCell sx={{ fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase' }}>Active</TableCell>
                                <TableCell sx={{ fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase' }}>Approval Status</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                [...Array(5)].map((_, i) => (
                                    <TableRow key={i}><TableCell colSpan={6}><Skeleton height={60} /></TableCell></TableRow>
                                ))
                            ) : (
                                companies.map((row) => (
                                    <TableRow key={row.company_uuid} hover>
                                        <TableCell>
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <Avatar variant="rounded" sx={{ bgcolor: '#e7f3ed', color: '#0fbd69', fontWeight: 700 }}>
                                                    {row.company_name?.charAt(0)}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{row.company_name}</Typography>
                                                    <Typography variant="caption" sx={{ color: '#4c9a74' }}>ID: {row.id}</Typography>
                                                </Box>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{row.email}</Typography>
                                            <Typography variant="caption" color="text.secondary">{row.mobile}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>GST: {row.gst_no}</Typography>
                                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>PAN: {row.pan_no}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={row.is_active ? "Active" : "Inactive"} size="small" color={row.is_active ? "success" : "default"} />
                                        </TableCell>
                                        <TableCell>
                                            {(() => {
                                                const statusChip = getStatusChipProps(row.approval_status);
                                                const remarkText = row.remark ? row.remark : "No remark";
                                                return (
                                                    <Tooltip title={remarkText}>
                                                        <Chip
                                                            label={statusChip.label}
                                                            size="small"
                                                            color={statusChip.color}
                                                            icon={statusChip.icon}
                                                        />
                                                    </Tooltip>
                                                );
                                            })()}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                                                <Tooltip title="View Details">
                                                    <IconButton size="small" onClick={() => handleOpenDetails(row)} sx={{ color: '#4c9a74' }}>
                                                        <Visibility fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Update Status">
                                                    <IconButton size="small" onClick={() => handleOpenStatusDialog(row)} sx={{ color: '#0fbd69' }}>
                                                        <VerifiedUserIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Edit">
                                                    <IconButton size="small" onClick={() => handleEdit(row.company_uuid)} sx={{ color: '#0fbd69' }}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Card>

            {/* Company Details Drawer */}
            <Drawer
                anchor="right"
                open={detailsDrawerOpen}
                onClose={handleCloseDetails}
                PaperProps={{ sx: { width: { xs: '100%', sm: 720, lg: 860 }, p: 3, borderLeft: '1px solid #e7f3ed' } }}
            >
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 900, color: '#0d1b15' }}>Company Profile</Typography>
                    <IconButton onClick={handleCloseDetails} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {detailsCompany && (
                    <Box sx={{ overflowY: 'auto' }}>
                        {/* Header Badge */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 4, p: 2, bgcolor: '#f8fbfa', borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar variant="rounded" sx={{ width: 56, height: 56, bgcolor: '#0fbd69' }}>
                                    <BusinessIcon fontSize="large" />
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{detailsCompany.company_name}</Typography>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Chip label={detailsCompany.is_active ? "Active" : "Inactive"} size="small" color={detailsCompany.is_active ? "success" : "default"} sx={{ height: 20, fontSize: 10 }} />
                                        {(() => {
                                            const statusChip = getStatusChipProps(detailsCompany.approval_status);
                                            const remarkText = detailsCompany.remark ? detailsCompany.remark : "No remark";
                                            return (
                                                <Tooltip title={remarkText}>
                                                    <Chip
                                                        label={statusChip.label}
                                                        size="small"
                                                        color={statusChip.color}
                                                        icon={statusChip.icon}
                                                        sx={{ height: 20, fontSize: 10 }}
                                                    />
                                                </Tooltip>
                                            );
                                        })()}
                                    </Stack>
                                </Box>
                            </Box>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    onClick={() => handleOpenStatusDialog(detailsCompany)}
                                    sx={{ textTransform: 'none', fontWeight: 700, minWidth: 140 }}
                                >
                                    Update Status
                                </Button>
                            </Stack>
                        </Box>

                        {/* Basic Contact Info */}
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BusinessIcon fontSize="small" color="primary" /> Basic Information
                        </Typography>
                        <DetailItem label="Email" value={detailsCompany.email} />
                        <DetailItem label="Mobile" value={detailsCompany.mobile} />
                        <DetailItem label="Referral Name" value={detailsCompany.referral_name} />

                        <Divider sx={{ my: 3 }} />

                        {/* Address */}
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationIcon fontSize="small" color="primary" /> Location details
                        </Typography>
                        <DetailItem label="Full Address" value={detailsCompany.address} />
                        <Grid container spacing={2}>
                            <Grid item xs={6}><DetailItem label="City" value={detailsCompany.city} /></Grid>
                            <Grid item xs={6}><DetailItem label="State" value={detailsCompany.state} /></Grid>
                            <Grid item xs={6}><DetailItem label="Pincode" value={detailsCompany.pincode} /></Grid>
                        </Grid>

                        <Divider sx={{ my: 3 }} />

                        {/* Legal & Banking */}
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BankIcon fontSize="small" color="primary" /> Legal & Banking
                        </Typography>
                        <DetailItem label="GST Number" value={detailsCompany.gst_no} />
                        <DetailItem label="PAN Number" value={detailsCompany.pan_no} />
                        <DetailItem label="Bank Account" value={detailsCompany.bank_account_no} />
                        <DetailItem label="IFSC Code" value={detailsCompany.bank_ifsc_code} />

                        <Divider sx={{ my: 3 }} />

                        {/* Approval Info */}
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <VerifiedUserIcon fontSize="small" color="primary" /> Approval details
                        </Typography>
                        <DetailItem label="Approval Status" value={getStatusChipProps(detailsCompany.approval_status).label} />
                        {getApprovalStatus(detailsCompany.approval_status) === 'rejected' && (
                            <DetailItem label="Rejection Remark" value={detailsCompany.remark} />
                        )}
                        {detailsCompany.approved_by && (
                            <DetailItem label="Approved By" value={detailsCompany.approved_by} />
                        )}
                        {detailsCompany.approved_at && (
                            <DetailItem label="Approved At" value={detailsCompany.approved_at} />
                        )}

                        <Box sx={{ mt: 4, mb: 2 }}>
                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={() => handleEdit(detailsCompany.company_uuid)}
                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                            >
                                Edit Profile
                            </Button>
                        </Box>

                    </Box>
                )}
            </Drawer>

            {/* Status Update Dialog */}
            <Dialog
                open={statusDialogOpen}
                onClose={handleCloseStatusDialog}
                PaperProps={{ sx: { borderRadius: 3, width: '100%', maxWidth: 520 } }}
            >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 2 }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 900, color: '#0d1b15' }}>Update Company Status</Typography>
                    <Typography variant="caption" sx={{ color: '#4c9a74' }}>
                        {selectedCompany?.company_name || 'Selected Company'}
                    </Typography>
                </Box>
                <IconButton onClick={handleCloseStatusDialog} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Remark *"
                                multiline
                                rows={3}
                                value={approvalRemark}
                                onChange={(e) => setApprovalRemark(e.target.value)}
                                sx={{ m: 1 }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end">
                                <Button
                                    variant="outlined"
                                    color="error"
                                    disabled={approvalLoading}
                                    onClick={() => handleApprovalUpdate("rejected")}
                                    sx={{ textTransform: 'none', fontWeight: 700, minWidth: 140 }}
                                >
                                    Reject
                                </Button>
                                <Button
                                    variant="contained"
                                    color="success"
                                    disabled={approvalLoading}
                                    onClick={() => handleApprovalUpdate("approved")}
                                    sx={{ textTransform: 'none', fontWeight: 700, minWidth: 140 }}
                                >
                                    Approve
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </DialogContent>
        </Dialog>
        </Box>
    );
};

export default CompanyList;
